/**
 * Superset of some methods from @keycloak/keycloak-account-ui to (de)aggregate keycloak and donor service backends
 * for use on the PersonalInfo page.
 *
 * @see https://github.com/keycloak/keycloak/blob/main/js/apps/account-ui/src/api/methods.ts
 */
import type {UserFormFields} from "@keycloak/keycloak-ui-shared";
import {
    getPersonalInfo as kGetPersonalInfo,
    savePersonalInfo as kSavePersonalInfo,
    type UserProfileMetadata,
} from "@keycloak/keycloak-account-ui";
import type {
    IAMDonorReplyJsonSchema,
    IAMTaxReceiptJsonSchema,
} from "../donor-service";
import {KeycloakContextWithDonorService} from "../context.tsx";

type CallOptions = {
    context: KeycloakContextWithDonorService;
    signal?: AbortSignal;
}

type DonorServiceUserRepresentation = {
    firstName?: string;
    lastName?: string;
    email?: string;
    attributes: {
        phone?: string;
        dwelling?: string;
        residence?: string;
        street?: string;
        postcode?: string;
        city?: string;
        country?: string;
        addressUndeliverable?: boolean;
    }
}

type KeycloakUserRepresentation = Omit<UserFormFields, 'attributes'> & {
    userProfileMetadata?: UserProfileMetadata;
    attributes: Record<string, string | string[]>;
}

export type UserRepresentation =
    & Omit<KeycloakUserRepresentation, 'attributes'>
    & Omit<DonorServiceUserRepresentation, 'attributes'>
    & {
        attributes:
            | DonorServiceUserRepresentation['attributes']
            | KeycloakUserRepresentation['attributes']
    };

function normalizeUserRepresentation(data: IAMDonorReplyJsonSchema): UserRepresentation {
    const {
        id,
        personalInformation: {
            firstname: firstName,
            lastname: lastName,
        },
        contactDetails: {
            email,
            phone,
            address: {
                dwelling,
                residence,
                street,
                postcode,
                city,
                country = 'FR',
                undeliverable: addressUndeliverable,
            } = {},
        },
    } = data;
    return {
        username: String(id),
        firstName,
        lastName,
        email,
        attributes: {
            phone,
            dwelling,
            residence,
            street,
            postcode,
            city,
            country,
            addressUndeliverable,
        }
    }
}

function extractPersonalInfo(data: UserRepresentation): IAMDonorReplyJsonSchema["personalInformation"] {
    return {
        firstname: data.firstName ?? '',
        lastname: data.lastName ?? '',
    };
}

function extractContactDetails({
    email = '',
    attributes: {
        phone,
        dwelling,
        residence,
        street,
        postcode,
        city,
        country,
    },
}: DonorServiceUserRepresentation): IAMDonorReplyJsonSchema["contactDetails"] {
    return {
        email,
        phone,
        address: { dwelling, residence, street, postcode, city, country },
    };
}

export async function getPersonalInfo({
    signal,
    context,
}: CallOptions): Promise<UserRepresentation> {
    const {donorService} = context;
    const [
        userProfile,
        donorData,
    ] = await Promise.all([
        kGetPersonalInfo({context, signal}) as Promise<KeycloakUserRepresentation>,
        donorService.fetchDonorRecord(),
    ]);
    const normalized = normalizeUserRepresentation(donorData);
    return {
        ...normalized,
        ...userProfile,
        attributes: {
            ...normalized.attributes,
            ...userProfile.attributes,
        },
    }
}

export async function getTaxReceiptLink(
    context: KeycloakContextWithDonorService,
    data: IAMTaxReceiptJsonSchema,
): Promise<URL> {
    const { donorService } = context;
    const { url } = await donorService.fetchTaxReceipt(data);
    return new URL(url);
}

export async function savePersonalInfo(
    context: KeycloakContextWithDonorService,
    data: UserRepresentation,
): Promise<void> {
    const { donorService } = context;
    const prevData = normalizeUserRepresentation(await donorService.fetchDonorRecord());
    const results = await Promise.allSettled([
        kSavePersonalInfo(context, data),
        personalInfoHasChanged(data, prevData)
            ? donorService.updatePersonalInfo(extractPersonalInfo(data))
            : undefined,
        contactDetailsHasChanged(data, prevData)
            ? donorService.updateContactDetails(extractContactDetails(data))
            : undefined,
    ]);

    const maybeErrors = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map((rejected) => rejected.reason);

    if (maybeErrors.length > 0) throw new AggregateError(maybeErrors, 'Unable to save changes');
}

function contactDetailsHasChanged(fields: UserRepresentation, prev: UserRepresentation): boolean {
    return fields.email !== prev.email
        || fields.attributes.phone !== prev.attributes.phone
        || fields.attributes.dwelling !== prev.attributes.dwelling
        || fields.attributes.residence !== prev.attributes.residence
        || fields.attributes.street !== prev.attributes.street
        || fields.attributes.postcode !== prev.attributes.postcode
        || fields.attributes.city !== prev.attributes.city
        || fields.attributes.country !== prev.attributes.country;
}

function personalInfoHasChanged(fields: UserRepresentation, prev: UserRepresentation): boolean {
    return fields.firstName !== prev.firstName
        || fields.lastName !== prev.lastName;
}
